// src/components/ProtectedRoute.js

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import MDBox from "components/MDBox";
import CircularProgress from "@mui/material/CircularProgress";
import AuthErrorPage from "./AuthErrorPage";

/**
 * ProtectedRoute component
 * Wraps routes that require authentication
 * Handles redirection based on environment
 */
const ProtectedRoute = ({ children }) => {
  const { isLoading, isAuthenticated, getLoginUrl, envConfig, authError } = useAuth();

  // Add state to track redirect attempts to prevent infinite loops
  const [hasAttemptedRedirect, setHasAttemptedRedirect] = useState(false);

  // IMPORTANT: All useEffect hooks must be called at the top level, before any conditional returns

  // Effect to track and limit redirect attempts
  useEffect(() => {
    // If not authenticated and not already attempted redirect, mark as attempted
    if (!isAuthenticated && !isLoading && !hasAttemptedRedirect) {
      setHasAttemptedRedirect(true);
    }
  }, [isAuthenticated, isLoading, hasAttemptedRedirect]);

  // For debugging auth issues - moved to top level
  useEffect(() => {
    console.log("Protected route auth status:", {
      isAuthenticated,
      isLoading,
      authError,
      path: window.location.pathname,
      hash: window.location.hash,
      env: envConfig,
    });
  }, [isAuthenticated, isLoading, authError, envConfig]);

  // Render logic starts here, after all hooks have been called
  if (isLoading) {
    // Show loading spinner while checking authentication
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </MDBox>
    );
  }

  if (!isAuthenticated) {
    // If there's a specific authentication error, show the error page
    if (authError) {
      console.log("Showing auth error page:", authError);
      return <AuthErrorPage error={authError} />;
    }

    // Prevent infinite redirects by checking if we've already attempted a redirect
    if (hasAttemptedRedirect) {
      console.log("Already attempted redirect, showing manual login option");
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <MDBox mb={3} fontWeight="bold" fontSize="1.2rem">
            Authentication Failed
          </MDBox>
          <MDBox mb={4}>Unable to verify your login. Please try logging in again.</MDBox>
          <MDBox
            component="a"
            href={getLoginUrl()}
            p={2}
            px={4}
            borderRadius="lg"
            bgcolor="info.main"
            color="white"
            sx={{ textDecoration: "none", cursor: "pointer" }}
          >
            Go to Login
          </MDBox>
        </MDBox>
      );
    }

    // Check if we're already at the login page
    const currentPath = window.location.pathname;
    const isLoginPage = currentPath.endsWith("/login");

    if (isLoginPage) {
      console.log("Already at login page, avoiding redirect");
      return null;
    }

    // In local development, use React Router navigation
    if (envConfig.localEnabled) {
      // Store the current location for potential redirect after login
      sessionStorage.setItem("redirectAfterLogin", currentPath);
      console.log("Local mode: navigating to /login");
      return <Navigate to="/login" replace />;
    } else {
      // For production/integration, redirect to the main site login
      const loginUrl = getLoginUrl();
      console.log("Redirecting to external login:", loginUrl);
      window.location.href = loginUrl;
      return null;
    }
  }

  // If authenticated, render the protected content
  return children;
};

// PropTypes validation
ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
